@file:NoLiveLiterals

package com.example.borshi.web.plans

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.data.MockPlans
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.model.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens
// FIX #4: Added missing sp20 and sp40 to complete the 4pt spacing scale
// ─────────────────────────────────────────────────────────────────────────────
private object DS {
    const val ink        = "#0D2B2E"
    const val inkMid     = "#3D5A5E"
    const val inkLight   = "#7A9598"
    const val teal       = "#26A69A"
    const val tealDeep   = "#1A7A72"
    const val tealGhost  = "#E0F2F1"
    const val tealFaint  = "#F0FAFA"
    const val crimson    = "#D11243"
    const val surface    = "#F5F9F8"
    const val white      = "#FFFFFF"
    const val border     = "#E0EDEB"
    const val borderDark = "#C4DBD8"

    val sp4  = 4.px;  val sp8  = 8.px;  val sp12 = 12.px; val sp16 = 16.px
    val sp20 = 20.px; val sp24 = 24.px; val sp32 = 32.px  // FIX #4: sp20 added
    val sp40 = 40.px; val sp48 = 48.px; val sp64 = 64.px  // FIX #4: sp40 added

    val r4   = 4.px; val r8   = 8.px;  val r12  = 12.px; val r16  = 16.px; val r24  = 24.px
    val rFull = 999.px

    const val shadowSm   = "0 1px 4px rgba(13,43,46,0.06)"
    const val shadowMd   = "0 4px 16px rgba(13,43,46,0.10)"
    const val shadowLg   = "0 8px 32px rgba(13,43,46,0.14)"
    const val shadowTeal = "0 4px 16px rgba(38,166,154,0.25)"
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Plans Screen
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun ProteinPlansScreen(
    cartStore: com.example.borshi.web.cart.CartStore? = null,
    onNavigate: ((com.example.borshi.web.Screen) -> Unit)? = null,
    onViewPlan: (NutritionistBasePlan) -> Unit = {}
) {

    DisposableEffect(Unit) {
        val style = kotlinx.browser.document.createElement("style")
        style.textContent = """
            @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
            *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'DM Sans', sans-serif; background: ${DS.surface}; color: ${DS.ink}; -webkit-font-smoothing: antialiased; }

            ::-webkit-scrollbar { width: 5px; height: 5px; }
            ::-webkit-scrollbar-track { background: transparent; }
            ::-webkit-scrollbar-thumb { background: ${DS.borderDark}; border-radius: 99px; }
            ::-webkit-scrollbar-thumb:hover { background: ${DS.teal}; }

            @keyframes fadeUp {
                from { opacity: 0; transform: translateY(20px); }
                to   { opacity: 1; transform: translateY(0); }
            }
            @keyframes shimmer {
                0%   { background-position: -600px 0; }
                100% { background-position:  600px 0; }
            }
            /* FIX #1: scaleIn animation added */
            @keyframes scaleIn {
                from { opacity: 0; transform: scale(0.95); }
                to   { opacity: 1; transform: scale(1); }
            }
            /* FIX #5: countUp animation for stat values */
            @keyframes countUp {
                from { opacity: 0; transform: translateY(6px); }
                to   { opacity: 1; transform: translateY(0); }
            }

            .fade-up  { animation: fadeUp  0.5s cubic-bezier(0.22,1,0.36,1) both; }
            .scale-in { animation: scaleIn 0.4s cubic-bezier(0.22,1,0.36,1) both; }
            .delay-1 { animation-delay: 0.05s; } .delay-2 { animation-delay: 0.10s; }
            .delay-3 { animation-delay: 0.15s; } .delay-4 { animation-delay: 0.20s; }
            .delay-5 { animation-delay: 0.25s; }

            .skeleton {
                background: linear-gradient(90deg,#E8F2F1 25%,#F4FAFA 50%,#E8F2F1 75%);
                background-size: 600px 100%;
                animation: shimmer 1.4s ease-in-out infinite;
                border-radius: 10px;
            }

            .plan-card {
                background: ${DS.white}; border-radius: 18px;
                border: 1.5px solid ${DS.border}; overflow: hidden;
                display: flex; flex-direction: column; cursor: pointer;
                transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
            }
            .plan-card:hover { transform: translateY(-5px); box-shadow: ${DS.shadowLg}; border-color: ${DS.teal}; }

            .nutri-card {
                background: ${DS.white}; border-radius: 12px;
                border: 1px solid ${DS.border}; overflow: hidden;
                transition: border-color 0.2s ease, box-shadow 0.2s ease;
                box-shadow: 0 1px 3px rgba(13,43,46,0.04);
            }
            .nutri-card:hover { border-color: ${DS.teal}; box-shadow: 0 4px 20px rgba(13,43,46,0.08); }
            .nutri-header { cursor: pointer; transition: background 0.2s ease; }
            .nutri-header:hover { background: ${DS.tealFaint}; }

            .diy-tile {
                background: ${DS.white}; border-radius: 16px; border: 2px solid ${DS.border};
                padding: 14px; display: flex; flex-direction: column;
                align-items: center; gap: 8px; text-align: center;
                cursor: pointer; position: relative;
                transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
            }
            .diy-tile:hover { transform: translateY(-3px); box-shadow: ${DS.shadowMd}; }
            .diy-tile.selected { background: ${DS.tealGhost}; border-color: ${DS.teal}; box-shadow: ${DS.shadowTeal}; }

            .tag-chip {
                display: inline-flex; align-items: center; gap: 5px;
                padding: 6px 14px; border-radius: 999px;
                font-size: 12px; font-weight: 600; cursor: pointer;
                border: 1.5px solid transparent; transition: all 0.15s ease;
                white-space: nowrap; font-family: 'DM Sans', sans-serif;
            }

            /* FIX #10: filter-chip hover and active both use teal for consistent signalling */
            .filter-chip {
                padding: 7px 18px; border-radius: 999px; font-size: 12px; font-weight: 600;
                cursor: pointer; border: 1.5px solid ${DS.border};
                background: ${DS.white}; color: ${DS.inkMid};
                font-family: 'DM Sans', sans-serif; transition: all 0.15s ease; white-space: nowrap;
            }
            .filter-chip:hover { border-color: ${DS.teal}; color: ${DS.teal}; background: ${DS.tealFaint}; }
            .filter-chip.active { background: ${DS.teal}; border-color: ${DS.teal}; color: white; box-shadow: ${DS.shadowTeal}; }

            .stat-bar {
                display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                gap: 1px; background: ${DS.border};
                border-radius: 16px; overflow: hidden; border: 1px solid ${DS.border};
            }
            .stat-cell { background: ${DS.white}; padding: 16px 12px; text-align: center; transition: background 0.2s ease; }
            .stat-cell.active { background: ${DS.tealFaint}; }
            /* FIX #5: stat value animates on re-render via key= */
            .stat-value { animation: countUp 0.25s ease both; }

            .section-label  { font-family: 'Syne', sans-serif; font-size: 11px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: ${DS.teal}; }
            .section-title  { font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800; color: ${DS.ink}; letter-spacing: -0.03em; line-height: 1.25; padding-bottom: 2px; overflow: visible; }
            .section-divider { height: 1px; background: linear-gradient(90deg, transparent, ${DS.borderDark}, transparent); margin: 56px 0; }

            .order-bar {
                position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
                z-index: 100; display: flex; align-items: center; gap: 16px;
                background: ${DS.ink}; color: white; border-radius: 999px;
                padding: 14px 20px 14px 24px;
                box-shadow: 0 8px 40px rgba(13,43,46,0.35);
                animation: fadeUp 0.3s ease both; white-space: nowrap;
                font-family: 'DM Sans', sans-serif;
            }

            .nav-tabs { display: flex; gap: 4px; background: ${DS.white}; border: 1.5px solid ${DS.border}; border-radius: 999px; padding: 4px; }
            .nav-tab  { padding: 8px 20px; border-radius: 999px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; background: transparent; color: ${DS.inkMid}; font-family: 'DM Sans', sans-serif; transition: all 0.15s ease; }
            .nav-tab.active { background: ${DS.ink}; color: white; }

            .hero-headline { font-family: 'Syne', sans-serif; font-size: clamp(36px,6vw,60px); font-weight: 800; color: ${DS.ink}; letter-spacing: -0.04em; line-height: 1.2; padding-bottom: 4px; overflow: visible; }

            /* FIX #7: sticker column hidden on mobile to prevent header overflow */
            @media (max-width: 640px) {
                .nav-tabs      { display: none; }
                .order-bar     { width: calc(100% - 32px); justify-content: space-between; }
                .stat-bar      { grid-template-columns: repeat(3, 1fr); }
                .nutri-stickers { display: none; }
            }
        """.trimIndent()
        kotlinx.browser.document.head?.appendChild(style)
        onDispose { style.remove() }
    }

    var allPlans      by remember { mutableStateOf<List<NutritionistBasePlan>>(emptyList()) }
    var nutritionists by remember { mutableStateOf<List<NutritionistProfile>>(emptyList()) }
    var allProducts   by remember { mutableStateOf<List<Product>>(emptyList()) }
    var isLoading     by remember { mutableStateOf(true) }
    var loadError     by remember { mutableStateOf<String?>(null) }
    var usingFallback by remember { mutableStateOf(false) }
    var activeSection by remember { mutableStateOf("diy") }
    var selectedNutritionistId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            // Load plans first — most critical
            val fetchedPlans = try {
                FirebaseService.getPublicPlans()
            } catch (e: Throwable) {
                if (e.message?.contains("left the composition") == true) return@LaunchedEffect
                console.warn("⚠️ Firebase plans load failed, using mock data:", e.message)
                null
            }

            if (fetchedPlans != null && fetchedPlans.isNotEmpty()) {
                allPlans = fetchedPlans
            } else {
                // Fallback to mock plans only when Firebase returns nothing
                allPlans = MockPlans.getPublicPlans()
                usingFallback = true
                if (fetchedPlans != null) {
                    console.log("ℹ️ No plans in Firebase yet — showing sample plans")
                }
            }

            // Load nutritionists and products — non-critical, best effort
            try { nutritionists = FirebaseService.getNutritionists() } catch (_: Throwable) {}
            try { allProducts   = FirebaseService.getProducts()      } catch (_: Throwable) {}

        } catch (e: Throwable) {
            if (e.message?.contains("left the composition") == true) return@LaunchedEffect
            loadError = "Unable to load plans. Please check your connection."
            if (allPlans.isEmpty()) {
                allPlans = MockPlans.getPublicPlans()
                usingFallback = true
            }
        } finally {
            isLoading = false
        }
    }

    // ── Skeleton loading ──────────────────────────────────────────────────
    if (isLoading) {
        Div({ style {
            minHeight(100.vh); backgroundColor(Color(DS.surface))
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
        }}) {
            Div({ style { height(72.px); backgroundColor(Color(DS.white)); property("border-bottom","1px solid ${DS.border}") }}) {}
            Div({ style { maxWidth(1200.px); property("margin","0 auto"); padding(40.px, 24.px); width(100.percent) }}) {
                Div({ classes("skeleton"); style { height(280.px); marginBottom(48.px) }}) {}
                Div({ style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns","repeat(auto-fill,minmax(240px,1fr))")
                    gap(16.px)
                }}) {
                    repeat(6) {
                        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px) }}) {
                            Div({ classes("skeleton"); style { height(160.px); borderRadius(18.px) }}) {}
                            Div({ classes("skeleton"); style { height(16.px); width(70.percent) }}) {}
                            Div({ classes("skeleton"); style { height(12.px); width(50.percent) }}) {}
                            Div({ classes("skeleton"); style { height(36.px) }}) {}
                        }
                    }
                }
            }
        }
        return
    }

    Div({ style { backgroundColor(Color(DS.surface)); minHeight(100.vh) }}) {

        // ── Sticky sub-nav ───────────────────────────────────────────────
        Div({ style {
            position(Position.Sticky); top(0.px); property("z-index","50")
            backgroundColor(Color("rgba(245,249,248,0.97)"))
            property("backdrop-filter","blur(20px)")
            property("-webkit-backdrop-filter","blur(20px)")
            property("border-bottom","1px solid rgba(13,43,46,0.07)")
            property("box-shadow","0 2px 16px rgba(13,43,46,0.04)")
        }}) {
            Div({ style {
                maxWidth(1200.px); property("margin","0 auto")
                padding(0.px, 24.px); height(60.px)
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
            }}) {
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(15.px); fontWeight("800")
                    color(Color(DS.ink)); property("letter-spacing","-0.03em")
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
                }}) {
                    Span({ style { fontSize(16.px) } }) { Text("🐙") }
                    Text("Protein Plans")
                }
                Div({ classes("nav-tabs") }) {
                    listOf("diy" to "DIY Builder", "plans" to "All Plans", "experts" to "Our Expert Panel")
                        .forEach { (key, label) ->
                            Button({
                                attr("class", if (activeSection == key) "nav-tab active" else "nav-tab")
                                onClick {
                                    activeSection = key
                                    kotlinx.browser.document.getElementById(key)
                                        ?.scrollIntoView(js("({behavior:'smooth',block:'start'})"))
                                }
                            }) { Text(label) }
                        }
                }
            }
        }

        // FIX #6: IntersectionObserver keeps nav highlight in sync with scroll position
        DisposableEffect(Unit) {
            val observer = js("""
                new IntersectionObserver(function(entries) {
                    entries.forEach(function(entry) {
                        if (entry.isIntersecting) {
                            window.dispatchEvent(
                                new CustomEvent('borshiSectionVisible', { detail: entry.target.id })
                            );
                        }
                    });
                }, { rootMargin: '-30% 0px -60% 0px', threshold: 0 })
            """)
            val handler: (dynamic) -> Unit = { e -> activeSection = e.detail as String }
            kotlinx.browser.window.addEventListener("borshiSectionVisible", handler)
            listOf("diy", "plans", "experts").forEach { id ->
                kotlinx.browser.document.getElementById(id)?.let { observer.observe(it) }
            }
            onDispose {
                kotlinx.browser.window.removeEventListener("borshiSectionVisible", handler)
                observer.disconnect()
            }
        }

        Div({ style { maxWidth(1200.px); property("margin","0 auto"); padding(0.px, 24.px, 80.px) }}) {

            if (loadError != null) {
                Div({ style {
                    backgroundColor(Color("#FFF8F0")); border(1.px, LineStyle.Solid, Color("#FFD0A0"))
                    borderRadius(DS.r12); padding(12.px, 16.px); margin(24.px, 0.px)
                    color(Color("#A34A00")); fontSize(13.px); fontWeight("500")
                }}) { Text("⚠️ $loadError") }
            }

            // Fallback notice — shown only when using sample/mock plans
            if (usingFallback && loadError == null) {
                Div({ style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(8.px)
                    backgroundColor(Color(DS.tealGhost)); border(1.px, LineStyle.Solid, Color(DS.teal))
                    borderRadius(DS.r12); padding(10.px, 16.px); margin(24.px, 0.px)
                    color(Color(DS.tealDeep)); fontSize(13.px); fontWeight("500")
                }}) { Text("ℹ️ Showing sample plans — live plans from our nutritionists will appear here soon.") }
            }

            // ── Hero ─────────────────────────────────────────────────────
            // Row 1: Left: Nutrition headline. Right: amino.png as large banner.
            // Row 2: Left: DIY Mix Builder. Right: Stats (📝 Plans, 👨‍⚕️ Experts, 🐟 Products) — same level, below amino.
            Div({ style {
                padding(56.px, 0.px, 48.px)
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(40.px)
            }}) {
                Div({ style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceBetween); gap(40.px); flexWrap(FlexWrap.Wrap)
                }}) {
                    Div({ classes("fade-up"); style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.FlexStart); flex(1); minWidth(280.px); overflow("visible") }}) {
                        Div({ classes("section-label"); style { marginBottom(14.px) }}) {
                            Text("Fresh · Personalised · Expert-crafted")
                        }
                        H1({ classes("hero-headline"); style { paddingBottom(6.px) }}) {
                            Text("Nutrition,")
                            Br {}
                            Span({ style { color(Color(DS.teal)) }}) { Text("done right.") }
                        }
                        P({ style {
                            fontSize(16.px); color(Color(DS.inkLight)); marginTop(16.px)
                            fontWeight("400"); maxWidth(480.px); lineHeight("1.6")
                        }}) {
                            Text("Science-backed plans designed by certified nutritionists — or build your own custom seafood mix.")
                        }
                    }
                    Div({ classes("fade-up","delay-2"); style {
                        flex(1); minWidth(320.px); maxWidth(480.px)
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    }}) {
                        Img(src = "https://firebasestorage.googleapis.com/v0/b/borshi-6fafe.firebasestorage.app/o/page_images%2Fplans%2Famino.png?alt=media", attrs = {
                            style {
                                width(100.percent); maxHeight(280.px)
                                property("object-fit","contain"); property("object-position","center")
                            }
                            attr("alt","Amino acid molecule")
                        })
                    }
                }
                // Row 2: DIY Mix Builder + Stats at same level, below amino
                Div({ style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Baseline)
                    justifyContent(JustifyContent.SpaceBetween); flexWrap(FlexWrap.Wrap); gap(24.px)
                }}) {
                    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp4); overflow("visible") }}) {
                        Div({ classes("section-label") }) { Text("Build Your Own") }
                        Div({ classes("section-title"); style { marginBottom(0.px); paddingBottom(4.px) }}) { Text("DIY Mix Builder") }
                    }
                    Div({ style {
                        display(DisplayStyle.Flex); gap(DS.sp24); alignItems(AlignItems.Center)
                        flexWrap(FlexWrap.Wrap)
                    }}) {
                        listOf(
                            Triple("📝 ${allPlans.size}+",     "Plans",    DS.teal),
                            Triple("👨‍⚕️ ${nutritionists.size}", "Experts",  DS.ink),
                            Triple("🐟 ${allProducts.size}+",  "Products", DS.tealDeep)
                        ).forEach { (num, label, color) ->
                            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Baseline); gap(6.px) }}) {
                                Span({ style {
                                    fontFamily("Syne, sans-serif"); fontSize(24.px)
                                    fontWeight("800"); color(Color(color)); lineHeight("1")
                                }}) { Text(num) }
                                Span({ style {
                                    fontSize(12.px); fontWeight("600")
                                    color(Color(DS.inkLight)); marginLeft(4.px)
                                }}) { Text(label) }
                            }
                        }
                    }
                }
            }

            // ── DIY Builder (first) ───────────────────────────────────────
            Div({ id("diy") }) {
                DiySection(allProducts, allPlans, nutritionists, cartStore, onNavigate)
            }

            Div({ classes("section-divider") }) {}

            // ── All Plans ─────────────────────────────────────────────────
            Div({ id("plans") }) {
                SectionHeader("Browse", "All Plans", "Filter by goal, dietician, and find your perfect match")
                AllPlansGrid(allPlans, nutritionists, selectedNutritionistId, onNutritionistFilterChange = { selectedNutritionistId = it }, onViewPlan)
            }

            Div({ classes("section-divider") }) {}

            // ── Our Expert Panel ───────────────────────────────────────────
            Div({ id("experts") }) {
                ExpertsSectionHeader()
                if (nutritionists.isEmpty() && allPlans.isEmpty()) {
                    EmptyState("No plans available yet — check back soon!")
                } else {
                    // Build list of experts with plan counts (including orphan plans)
                    val expertEntries = buildList {
                        nutritionists.forEach { doc ->
                            val count = allPlans.count { it.nutritionistId == doc.nutritionistId }
                            add(doc to count)
                        }
                        val orphanCount = allPlans.count { p -> nutritionists.none { it.nutritionistId == p.nutritionistId } }
                        if (orphanCount > 0) {
                            add(NutritionistProfile(
                                nutritionistId = "borshi",
                                name           = "Borshi Nutrition Team",
                                specialization = "General Nutrition",
                                qualifications = "In-House Certified Team"
                            ) to orphanCount)
                        }
                    }
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(DS.sp8)
                        }
                    }) {
                        Button({
                            style {
                                flexShrink(0); width(40.px); height(40.px); padding(0.px)
                                borderRadius(DS.rFull); backgroundColor(Color(DS.border))
                                color(Color(DS.ink)); border(0.px)
                                fontSize(18.px); fontWeight("700"); cursor("pointer")
                            }
                            onClick {
                                kotlinx.browser.document.getElementById("experts-scroll")?.let { el ->
                                    (el.asDynamic()).scrollBy(-320.0, 0.0)
                                }
                            }
                        }) { Text("<") }
                        Div({
                            id("experts-scroll")
                            style {
                                flex(1)
                                display(DisplayStyle.Flex)
                                gap(12.px)
                                overflowX("auto")
                                overflowY("hidden")
                                property("scroll-behavior", "smooth")
                                property("scrollbar-width", "none")
                                property("-ms-overflow-style", "none")
                            }
                        }) {
                            expertEntries.forEach { (doc, planCount) ->
                                Div({ style { flexShrink(0); width(280.px) }}) {
                                    NutritionistCard(doc, planCount) {
                                        selectedNutritionistId = doc.nutritionistId
                                        activeSection = "plans"
                                        kotlinx.browser.document.getElementById("plans")
                                            ?.scrollIntoView(js("({behavior:'smooth',block:'start'})"))
                                    }
                                }
                            }
                        }
                        Button({
                            style {
                                flexShrink(0); width(40.px); height(40.px); padding(0.px)
                                borderRadius(DS.rFull); backgroundColor(Color(DS.border))
                                color(Color(DS.ink)); border(0.px)
                                fontSize(18.px); fontWeight("700"); cursor("pointer")
                            }
                            onClick {
                                kotlinx.browser.document.getElementById("experts-scroll")?.let { el ->
                                    (el.asDynamic()).scrollBy(320.0, 0.0)
                                }
                            }
                        }) { Text(">") }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Section Header
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun SectionHeader(label: String, title: String, subtitle: String) {
    Div({ style { marginBottom(DS.sp32) }}) {
        Div({ classes("section-label"); style { marginBottom(DS.sp8) }}) { Text(label) }
        Div({ classes("section-title") }) { Text(title) }
        P({ style { fontSize(14.px); color(Color(DS.inkLight)); marginTop(8.px); fontWeight("400") }}) {
            Text(subtitle)
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Experts Section Header — refined, minimal (Apple/Meta style)
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun ExpertsSectionHeader() {
    Div({ style { marginBottom(DS.sp40) }}) {
        Div({ style {
            fontFamily("Syne, sans-serif"); fontSize(22.px); fontWeight("700")
            color(Color(DS.ink)); letterSpacing("-0.02em"); marginBottom(6.px)
        }}) { Text("Our Expert Panel") }
        P({ style {
            fontSize(15.px); color(Color(DS.inkLight)); fontWeight("400")
            lineHeight("1.5"); maxWidth(420.px)
        }}) {
            Text("Plans curated by certified nutritionists. Tap to explore.")
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty State
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun EmptyState(message: String) {
    Div({ style {
        textAlign("center"); padding(64.px, 0.px)
        color(Color(DS.inkLight)); fontSize(15.px); fontWeight("400")
    }}) {
        Div({ style { fontSize(40.px); marginBottom(12.px) }}) { Text("🌿") }
        Text(message)
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// All Plans Grid
// ─────────────────────────────────────────────────────────────────────────────

enum class PlanProtocol(val displayName: String, val categories: List<PlanCategory>) {
    FITNESS("Fitness", listOf(PlanCategory.MUSCLE_BUILDING, PlanCategory.ATHLETE)),
    MEDICAL("Health", listOf(PlanCategory.DIABETES, PlanCategory.SENIOR, PlanCategory.PREGNANCY)),
    LIFESTYLE("Lifestyle", listOf(PlanCategory.GENERAL_HEALTH, PlanCategory.WEIGHT_LOSS))
}

@Composable
private fun AllPlansGrid(
    plans: List<NutritionistBasePlan>,
    nutritionists: List<NutritionistProfile>,
    selectedNutritionistId: String?,
    onNutritionistFilterChange: (String?) -> Unit,
    onViewPlan: (NutritionistBasePlan) -> Unit
) {
    var selectedProtocol by remember { mutableStateOf<PlanProtocol?>(null) }
    var selectedCategory by remember { mutableStateOf<PlanCategory?>(null) }
    var isMonthly by remember { mutableStateOf(false) }

    // Build unique dietician list from plans (includes orphan "Borshi Team")
    val dieticianOptions = remember(plans, nutritionists) {
        val fromPlans = plans.map { p ->
            val profile = nutritionists.find { it.nutritionistId == p.nutritionistId }
            p.nutritionistId to (profile?.name ?: p.nutritionistName.ifBlank { "Borshi Team" })
        }.distinctBy { it.first }
        fromPlans
    }

    val visibleCategories = selectedProtocol?.categories ?: PlanCategory.entries
    val filtered = plans.filter { plan ->
        val protocolMatch = selectedProtocol == null || selectedProtocol!!.categories.contains(plan.category)
        val categoryMatch = selectedCategory == null || plan.category == selectedCategory
        val nutritionistMatch = selectedNutritionistId == null || plan.nutritionistId == selectedNutritionistId
        protocolMatch && categoryMatch && nutritionistMatch
    }

    // ── Comparison Toggle ─────────────────────────────────────────────────────
    Div({ style {
        display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center); marginBottom(DS.sp24)
    }}) {
        // Toggle Switch
        Div({
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(DS.sp12)
                backgroundColor(Color(DS.white)); padding(4.px); borderRadius(DS.rFull)
                border(1.px, LineStyle.Solid, Color(DS.border))
            }
        }) {
            listOf(false to "Weekly", true to "Monthly").forEach { (isMonth, label) ->
                Div({
                    style {
                        padding(6.px, 16.px); borderRadius(DS.rFull); cursor("pointer")
                        fontSize(13.px); fontWeight("600")
                        backgroundColor(if (isMonthly == isMonth) Color(DS.tealGhost) else Color.transparent)
                        color(if (isMonthly == isMonth) Color(DS.tealDeep) else Color(DS.inkLight))
                        property("transition", "all 0.2s ease")
                    }
                    onClick { isMonthly = isMonth }
                }) { Text(label) }
            }
        }

        if (isMonthly) {
            Span({ style {
                fontSize(12.px); color(Color(DS.crimson)); fontWeight("700")
                backgroundColor(Color("#FFEBEE")); padding(4.px, 10.px); borderRadius(DS.rFull)
            }}) { Text("Save 15%") }
        }
    }

    // ── Dietician filter ─────────────────────────────────────────────────────
    if (dieticianOptions.size > 1) {
        Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp16); alignItems(AlignItems.Center) }}) {
            Span({ style { fontSize(12.px); fontWeight("700"); color(Color(DS.teal)); property("letter-spacing","0.08em"); marginRight(4.px) }}) { Text("BY EXPERT") }
            Button({
                attr("class", if (selectedNutritionistId == null) "filter-chip active" else "filter-chip")
                onClick { onNutritionistFilterChange(null) }
            }) { Text("All Experts") }
            dieticianOptions.forEach { (id, name) ->
                Button({
                    attr("class", if (selectedNutritionistId == id) "filter-chip active" else "filter-chip")
                    onClick { onNutritionistFilterChange(if (selectedNutritionistId == id) null else id) }
                }) { Text("👨‍⚕️ $name") }
            }
        }
    }

    // ── Protocol Chips ────────────────────────────────────────────────────────
    Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp16) }}) {
        Button({
            attr("class", if (selectedProtocol == null) "filter-chip active" else "filter-chip")
            onClick { selectedProtocol = null; selectedCategory = null }
        }) { Text("All Protocols") }
        PlanProtocol.entries.forEach { protocol ->
            Button({
                attr("class", if (selectedProtocol == protocol) "filter-chip active" else "filter-chip")
                onClick {
                    selectedProtocol = if (selectedProtocol == protocol) null else protocol
                    selectedCategory = null
                }
            }) { Text(protocol.displayName) }
        }
    }

    // ── Category Chips ────────────────────────────────────────────────────────
    if (selectedProtocol != null) {
        Div({ style {
            display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap)
            marginBottom(DS.sp32); marginLeft(DS.sp16)
            property("border-left", "2px solid ${DS.border}"); paddingLeft(DS.sp16)
        }}) {
            Button({
                attr("class", if (selectedCategory == null) "tag-chip" else "tag-chip")
                style {
                    backgroundColor(if (selectedCategory == null) Color(DS.ink) else Color(DS.border))
                    color(if (selectedCategory == null) Color.white else Color(DS.inkMid))
                }
                onClick { selectedCategory = null }
            }) { Text("All in ${selectedProtocol!!.displayName}") }

            visibleCategories.forEach { cat ->
                Button({
                    attr("class", if (selectedCategory == cat) "tag-chip" else "tag-chip")
                    style {
                        backgroundColor(if (selectedCategory == cat) Color(DS.teal) else Color(DS.white))
                        color(if (selectedCategory == cat) Color.white else Color(DS.inkMid))
                        border(1.px, LineStyle.Solid, if (selectedCategory == cat) Color(DS.teal) else Color(DS.border))
                    }
                    onClick { selectedCategory = cat }
                }) { Text("${planCategoryEmoji(cat)} ${cat.displayName}") }
            }
        }
    } else {
         Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp32) }}) {
            PlanCategory.entries.take(6).forEach { cat ->
                 Button({
                    attr("class", if (selectedCategory == cat) "filter-chip active" else "filter-chip")
                    onClick { selectedCategory = if (selectedCategory == cat) null else cat }
                }) { Text("${planCategoryEmoji(cat)} ${cat.displayName}") }
            }
         }
    }

    if (filtered.isEmpty()) { EmptyState("No plans match this filter."); return }

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(DS.sp8)
        }
    }) {
        Button({
            style {
                flexShrink(0); width(40.px); height(40.px); padding(0.px)
                borderRadius(DS.rFull); backgroundColor(Color(DS.border))
                color(Color(DS.ink)); border(0.px)
                fontSize(18.px); fontWeight("700"); cursor("pointer")
            }
            onClick {
                kotlinx.browser.document.getElementById("all-plans-scroll")?.let { el ->
                    (el.asDynamic()).scrollBy(-300.0, 0.0)
                }
            }
        }) { Text("<") }
        Div({
            id("all-plans-scroll")
            style {
                flex(1)
                display(DisplayStyle.Flex)
                gap(DS.sp24)
                overflowX("auto")
                overflowY("hidden")
                property("scroll-behavior", "smooth")
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
        }) {
            filtered.forEachIndexed { i, plan ->
                Div({
                    classes("fade-up", "delay-${(i % 5) + 1}")
                    style { flexShrink(0); width(280.px) }
                }) {
                    PlanCard(plan, DS.teal, isMonthly, onViewPlan)
                }
            }
        }
        Button({
            style {
                flexShrink(0); width(40.px); height(40.px); padding(0.px)
                borderRadius(DS.rFull); backgroundColor(Color(DS.border))
                color(Color(DS.ink)); border(0.px)
                fontSize(18.px); fontWeight("700"); cursor("pointer")
            }
            onClick {
                kotlinx.browser.document.getElementById("all-plans-scroll")?.let { el ->
                    (el.asDynamic()).scrollBy(300.0, 0.0)
                }
            }
        }) { Text(">") }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Unified Plan Card
// FIX #9: Category pill uses planCategoryStyle bg colour — not hardcoded white
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun PlanCard(
    plan: NutritionistBasePlan,
    @Suppress("UNUSED_PARAMETER") accentColor: String,
    isMonthly: Boolean = false,
    onViewPlan: (NutritionistBasePlan) -> Unit
) {
    // FIX #9: both bg and fg now used — bg was previously discarded with _
    val (stickerBg, stickerFg) = planCategoryStyle(plan.category)
    val displayPrice = if (isMonthly) plan.pricingTemplate.weeklyBase * 4 * 0.85 else plan.pricingTemplate.weeklyBase

    Div({ classes("plan-card"); onClick { onViewPlan(plan) } }) {
        // Thumbnail
        Div({ style {
            width(100.percent); height(160.px); position(Position.Relative); overflow("hidden")
            backgroundColor(Color(DS.tealGhost))
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
        }}) {
            val imgUrl = plan.imageUrl.takeIf { it.isNotBlank() && it.startsWith("http") } ?: com.example.borshi.web.firebase.FirebaseService.getPlanImageUrl(plan.category)
            Img(src = imgUrl, attrs = {
                style { width(100.percent); height(100.percent); property("object-fit","cover") }
                attr("alt", plan.planName)
                attr("onerror", "this.style.display='none';var n=this.nextElementSibling;if(n)n.style.display='flex'")
            })
            Span({ style { display(DisplayStyle.None); fontSize(52.px); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); width(100.percent); height(100.percent) }}) { Text(planCategoryEmoji(plan.category)) }
            // FIX #9: pill uses per-category stickerBg and stickerFg
            Div({ style {
                position(Position.Absolute); top(DS.sp12); left(DS.sp12)
                backgroundColor(Color(stickerBg)); color(Color(stickerFg))
                fontSize(11.px); fontWeight("700"); padding(4.px, 10.px); borderRadius(DS.rFull)
                property("box-shadow",DS.shadowSm)
            }}) { Text("${planCategoryEmoji(plan.category)} ${plan.category.displayName}") }

            // Founder's Choice Badge
            if (plan.nutritionistId == "borshi" || plan.category == PlanCategory.MUSCLE_BUILDING) {
                Div({ style {
                    position(Position.Absolute); top(DS.sp12); right(DS.sp12)
                    backgroundColor(Color(DS.ink)); color(Color(DS.white))
                    fontSize(10.px); fontWeight("800"); padding(4.px, 8.px); borderRadius(DS.r4)
                    property("box-shadow",DS.shadowSm); letterSpacing("0.05em"); property("text-transform", "uppercase")
                }}) { Text("RECOMMENDED") }
            }
        }

        // Body
        Div({ style {
            padding(DS.sp16); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
            gap(DS.sp8); flex(1)
        }}) {
            Div({ style {
                fontFamily("Syne, sans-serif"); fontSize(15.px); fontWeight("700")
                color(Color(DS.ink)); lineHeight("1.3")
            }}) { Text(plan.planName) }

            if (plan.nutritionistName.isNotBlank()) {
                Div({ style {
                    fontSize(12.px); fontWeight("600"); color(Color(DS.inkMid))
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(4.px)
                }}) {
                    Span({ style { fontSize(13.px) }}) { Text("👨‍⚕️") }
                    Text("by ${plan.nutritionistName}")
                }
            }

            // What's Inside Icons
            Div({ style { display(DisplayStyle.Flex); gap(DS.sp4); marginBottom(4.px) }}) {
               val icons = when(plan.category) {
                   PlanCategory.MUSCLE_BUILDING -> listOf("🐓", "🥚", "🐟")
                   PlanCategory.WEIGHT_LOSS, PlanCategory.DIABETES -> listOf("🐟", "🦐", "🥬")
                   else -> listOf("🐟", "🌿")
               }
               icons.forEach { icon ->
                   Span({ style { fontSize(14.px); backgroundColor(Color(DS.surface)); padding(2.px); borderRadius(4.px) }}) { Text(icon) }
               }
            }

            Div({ style { display(DisplayStyle.Flex); gap(DS.sp4); flexWrap(FlexWrap.Wrap) }}) {
                if (plan.nutritionalTargets.proteinPerWeek > 0)
                    NutriChip("🥩 ${plan.nutritionalTargets.proteinPerWeek}g", "#E8F5E9", "#2E7D32")
                if (plan.nutritionalTargets.caloriesPerDay > 0)
                    NutriChip("🔥 ${plan.nutritionalTargets.caloriesPerDay}cal", "#FFF3E0", "#BF360C")
            }

            if (plan.description.isNotBlank()) {
                P({ style {
                    fontSize(12.px); color(Color(DS.inkLight)); lineHeight("1.5")
                    property("display","-webkit-box"); property("-webkit-line-clamp","2")
                    property("-webkit-box-orient","vertical"); overflow("hidden"); fontWeight("400")
                }}) { Text(plan.description) }
            }

            Div({ style { flex(1) }}) {}

            // Price + CTA
            Div({ style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center); marginTop(DS.sp4)
                property("border-top","1px solid ${DS.border}"); paddingTop(DS.sp12)
            }}) {
                if (plan.pricingTemplate.weeklyBase > 0) {
                     Div({}) {
                        if (plan.pricingTemplate.customPricingRules.isNotEmpty() || true) { // Always show 'Starting at' for now
                             Div({ style { fontSize(10.px); color(Color(DS.inkLight)); fontWeight("600"); property("text-transform", "uppercase") }}) {
                                Text("Starting at")
                            }
                        }
                        Div({ style {
                            fontFamily("Syne, sans-serif"); fontSize(18.px)
                            fontWeight("800"); color(Color(DS.crimson)); lineHeight("1")
                        }}) {
                            Text("₹${displayPrice.toInt()}")
                            Span({ style { fontSize(11.px); color(Color(DS.inkLight)); fontWeight("400") }}) {
                                Text(if (isMonthly) "/mo" else "/wk")
                            }
                        }
                    }
                } else { Div {} }
                Button({
                    style {
                        padding(8.px, 16.px); borderRadius(DS.rFull); border(0.px)
                        backgroundColor(Color(DS.ink)); color(Color(DS.white))
                        fontSize(12.px); fontWeight("700"); cursor("pointer")
                        fontFamily("DM Sans, sans-serif")
                    }
                    onClick { it.stopPropagation(); onViewPlan(plan) }
                }) { Text("View →") }
            }
        }
    }
}


@Composable
private fun NutriChip(label: String, bg: String, fg: String) {
    Span({ style {
        fontSize(10.px); fontWeight("700"); padding(3.px, 8.px); borderRadius(DS.r8)
        backgroundColor(Color(bg)); color(Color(fg)); whiteSpace("nowrap")
    }}) { Text(label) }
}

// ─────────────────────────────────────────────────────────────────────────────
// Nutritionist Card — compact, click filters All Plans section
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun NutritionistCard(
    nutritionist: NutritionistProfile,
    planCount: Int,
    onClickExpert: () -> Unit
) {
    Div({ classes("nutri-card") }) {
        Div({
            classes("nutri-header")
            style {
                padding(DS.sp20)
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                gap(DS.sp16)
            }
            onClick { onClickExpert() }
        }) {
            Div({ style {
                width(48.px); height(48.px); borderRadius(50.percent); flexShrink(0)
                overflow("hidden"); backgroundColor(Color(DS.border))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            }}) {
                if (nutritionist.profileImage.isNotBlank()) {
                    Img(src = nutritionist.profileImage, attrs = {
                        style { width(100.percent); height(100.percent); property("object-fit","cover") }
                    })
                } else {
                    Span({ style {
                        fontFamily("Syne, sans-serif"); fontSize(18.px)
                        fontWeight("600"); color(Color(DS.inkMid))
                    }}) { Text(nutritionist.name.take(1).uppercase()) }
                }
            }

            Div({ style { flex(1); minWidth(120.px) }}) {
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(16.px)
                    fontWeight("600"); color(Color(DS.ink)); marginBottom(2.px)
                }}) { Text(nutritionist.name) }
                Div({ style {
                    fontSize(13.px); color(Color(DS.inkLight)); fontWeight("400")
                }}) { Text(nutritionist.specialization.ifBlank { "Nutrition Expert" }) }
                if (nutritionist.qualifications.isNotBlank()) {
                    Div({ style { fontSize(11.px); color(Color(DS.inkMid)); fontWeight("500"); marginTop(4.px) }}) {
                        Text(nutritionist.qualifications.take(40) + if (nutritionist.qualifications.length > 40) "…" else "")
                    }
                }
            }

            Div({ style {
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center); flexShrink(0)
            }}) {
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(22.px); fontWeight("800")
                    color(Color(DS.teal)); lineHeight("1")
                }}) { Text("$planCount") }
                Div({ style {
                    fontSize(11.px); fontWeight("600"); color(Color(DS.inkLight)); marginTop(2.px)
                }}) { Text("plan${if (planCount != 1) "s" else ""}") }
            }

            Span({ style {
                fontSize(16.px); color(Color(DS.teal)); fontWeight("700"); flexShrink(0)
            }}) { Text("→") }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// DIY Section
// Stats + DIY Mix Builder moved to hero (below amino). This section has the dark banner + builder.
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DiySection(
    allProducts: List<Product>,
    allPlans: List<NutritionistBasePlan>,
    nutritionists: List<NutritionistProfile>,
    cartStore: com.example.borshi.web.cart.CartStore? = null,
    onNavigate: ((com.example.borshi.web.Screen) -> Unit)? = null
) {
    P({ style { fontSize(14.px); color(Color(DS.inkLight)); marginBottom(DS.sp24); fontWeight("400") }}) {
        Text("Pick seafood, see live nutrition, build your custom plan")
    }

    Div({ style {
        property("background","linear-gradient(135deg,${DS.ink} 0%,#1A4A4E 100%)")
        borderRadius(DS.r24); padding(DS.sp24); marginBottom(DS.sp32)
        display(DisplayStyle.Flex); alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween); flexWrap(FlexWrap.Wrap); gap(DS.sp16)
        position(Position.Relative); overflow("hidden")
    }}) {
        // Decorative circle
        Div({ style {
            position(Position.Absolute); top((-40).px); right((-40).px)
            width(200.px); height(200.px); borderRadius(50.percent)
            property("background","rgba(38,166,154,0.12)")
        }}) {}

        // FIX #3: unique content — live product count + feature chips
        Div({ style {
            position(Position.Relative); display(DisplayStyle.Flex)
            alignItems(AlignItems.Center); gap(DS.sp16); flexWrap(FlexWrap.Wrap)
        }}) {
            Span({ style { fontSize(32.px) }}) { Text("🧪") }
            Div {
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(22.px); fontWeight("800")
                    color(Color(DS.white)); lineHeight("1"); marginBottom(DS.sp8)
                }}) {
                    Text("${allProducts.size} products")
                    Span({ style {
                        fontSize(13.px); fontWeight("400")
                        color(Color("rgba(255,255,255,0.6)")); marginLeft(8.px)
                    }}) { Text("available to mix") }
                }
                Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap) }}) {
                    listOf("Live nutrition tracking", "Omega-3 estimates", "Custom order").forEach { feat ->
                        Span({ style {
                            fontSize(11.px); fontWeight("600")
                            backgroundColor(Color("rgba(255,255,255,0.12)"))
                            color(Color("rgba(255,255,255,0.85)"))
                            padding(4.px, 10.px); borderRadius(DS.rFull)
                        }}) { Text("✓ $feat") }
                    }
                    Span({ style {
                        fontSize(11.px); fontWeight("600")
                        backgroundColor(Color("rgba(255,255,255,0.12)"))
                        color(Color("rgba(255,255,255,0.85)"))
                        padding(4.px, 10.px); borderRadius(DS.rFull)
                    }}) { Text("❄️ 0–4°C cold-chain delivery") }
                }
            }
        }
    }

    var userTargets by remember { mutableStateOf(UserTargets()) }
    DiyMixBuilderContent(allProducts, userTargets, { userTargets = it }, cartStore, onNavigate)
}

// ─────────────────────────────────────────────────────────────────────────────
// DIY Mix Builder Content
// FIX #5: stat values animate on change via attr("key", value)
// FIX #8: product tiles show up to 2 mini nutrition tags
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DiyMixBuilderContent(
    allProducts: List<Product>,
    userTargets: UserTargets,
    onUserTargetsChange: (UserTargets) -> Unit,
    cartStore: com.example.borshi.web.cart.CartStore? = null,
    onNavigate: ((com.example.borshi.web.Screen) -> Unit)? = null
) {
    var selected     by remember { mutableStateOf<Map<String, Int>>(emptyMap()) }
    var searchQuery  by remember { mutableStateOf("") }
    var activeTagKey by remember { mutableStateOf("Popular") }
    var activeTagVal by remember { mutableStateOf("") }

    // ── Goal selector + target sliders (Phase 1 MVP) ───────────────────────
    Div({ style {
        backgroundColor(Color(DS.white)); borderRadius(DS.r16)
        border(1.5.px, LineStyle.Solid, Color(DS.border))
        padding(DS.sp20); marginBottom(DS.sp24)
    }}) {
        Div({ style { fontSize(12.px); fontWeight("700"); color(Color(DS.teal)); marginBottom(DS.sp12); property("letter-spacing","0.08em") }}) {
            Text("SET YOUR GOAL")
        }
        Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp16) }}) {
            listOf(
                PlanCategory.MUSCLE_BUILDING,
                PlanCategory.HEART_HEALTH,
                PlanCategory.PREGNANCY,
                PlanCategory.WEIGHT_LOSS,
                PlanCategory.GENERAL_HEALTH
            ).forEach { cat ->
                Button({
                    attr("class", if (userTargets.goalPreset == cat) "filter-chip active" else "filter-chip")
                    onClick {
                        val def = UserTargets(goalPreset = cat, proteinPerDayGrams = UserTargets(goalPreset = cat).defaultProteinForGoal(), budgetPerWeekRupees = userTargets.budgetPerWeekRupees)
                        onUserTargetsChange(def)
                    }
                }) { Text("${planCategoryEmoji(cat)} ${cat.displayName}") }
            }
        }
        Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp12); maxWidth(400.px) }}) {
            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center) }}) {
                Span({ style { fontSize(13.px); fontWeight("600"); color(Color(DS.ink)) }}) { Text("Protein target") }
                Span({ style { fontSize(14.px); fontWeight("800"); color(Color(DS.teal)) }}) { Text("${userTargets.proteinPerDayGrams}g/day") }
            }
            Input(org.jetbrains.compose.web.attributes.InputType.Range) {
                value(userTargets.proteinPerDayGrams.toString())
                onInput { e -> onUserTargetsChange(userTargets.copy(proteinPerDayGrams = (e.target.asDynamic().value as String).toIntOrNull() ?: 100)) }
                attr("min", "50")
                attr("max", "250")
                style { width(100.percent); property("accent-color", DS.teal) }
            }
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(DS.sp8) }}) {
                Span({ style { fontSize(13.px); fontWeight("600"); color(Color(DS.ink)); flexShrink(0) }}) { Text("Budget (₹/week)") }
                Input(org.jetbrains.compose.web.attributes.InputType.Number) {
                    value(if (userTargets.budgetPerWeekRupees > 0) userTargets.budgetPerWeekRupees.toInt().toString() else "")
                    onInput { e ->
                        val n = (e.target.asDynamic().value as String).toDoubleOrNull() ?: 0.0
                        onUserTargetsChange(userTargets.copy(budgetPerWeekRupees = if (n > 0) n else 0.0))
                    }
                    style {
                        width(120.px); padding(8.px, 12.px)
                        borderRadius(DS.r8); border(1.5.px, LineStyle.Solid, Color(DS.border))
                        fontSize(14.px)
                    }
                    attr("placeholder", "No limit")
                    attr("min", "0")
                }
            }
        }
        // ── Health Focus toggles (micronutrients) ─────────────────────────────
        Div({ style { marginTop(DS.sp16); paddingTop(DS.sp16); property("border-top","1px solid ${DS.border}") }}) {
            Div({ style { fontSize(12.px); fontWeight("700"); color(Color(DS.teal)); marginBottom(DS.sp12); property("letter-spacing","0.08em") }}) {
                Text("HEALTH FOCUS (optional)")
            }
            Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap) }}) {
                listOf(
                    "maximizeOmega3" to "🐟 Maximize Ω3",
                    "prioritizeB12" to "💊 B12",
                    "prioritizeVitaminD" to "☀️ Vitamin D",
                    "prioritizeZinc" to "⚡ Zinc",
                    "prioritizeSelenium" to "❤️ Selenium",
                    "prioritizeIodine" to "🦋 Iodine",
                    "prioritizeMagnesium" to "💪 Magnesium",
                    "lowMercuryPregnancy" to "🤰 Low Mercury",
                    "lowCarbDiabetes" to "💉 Low Carb"
                ).forEach { (key, label) ->
                    val isOn = when (key) {
                        "maximizeOmega3" -> userTargets.maximizeOmega3
                        "prioritizeB12" -> userTargets.prioritizeB12
                        "prioritizeVitaminD" -> userTargets.prioritizeVitaminD
                        "prioritizeZinc" -> userTargets.prioritizeZinc
                        "prioritizeSelenium" -> userTargets.prioritizeSelenium
                        "prioritizeIodine" -> userTargets.prioritizeIodine
                        "prioritizeMagnesium" -> userTargets.prioritizeMagnesium
                        "lowMercuryPregnancy" -> userTargets.lowMercuryPregnancy
                        "lowCarbDiabetes" -> userTargets.lowCarbDiabetes
                        else -> false
                    }
                    Span({
                        attr("class", if (isOn) "filter-chip active" else "filter-chip")
                        onClick {
                            val next = when (key) {
                                "maximizeOmega3" -> userTargets.copy(maximizeOmega3 = !isOn, omega3PerWeekMg = if (!isOn) userTargets.defaultOmega3ForGoal() else 0)
                                "prioritizeB12" -> userTargets.copy(prioritizeB12 = !isOn)
                                "prioritizeVitaminD" -> userTargets.copy(prioritizeVitaminD = !isOn)
                                "prioritizeZinc" -> userTargets.copy(prioritizeZinc = !isOn)
                                "prioritizeSelenium" -> userTargets.copy(prioritizeSelenium = !isOn)
                                "prioritizeIodine" -> userTargets.copy(prioritizeIodine = !isOn)
                                "prioritizeMagnesium" -> userTargets.copy(prioritizeMagnesium = !isOn)
                                "lowMercuryPregnancy" -> userTargets.copy(lowMercuryPregnancy = !isOn)
                                "lowCarbDiabetes" -> userTargets.copy(lowCarbDiabetes = !isOn)
                                else -> userTargets
                            }
                            onUserTargetsChange(next)
                        }
                    }) { Text(label) }
                }
            }
            if (userTargets.maximizeOmega3 && userTargets.omega3PerWeekMg > 0) {
                Div({ style { marginTop(DS.sp12); display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(DS.sp8) }}) {
                    Span({ style { fontSize(12.px); fontWeight("600"); color(Color(DS.ink)) }}) { Text("Ω3 target") }
                    Input(org.jetbrains.compose.web.attributes.InputType.Number) {
                        value(userTargets.omega3PerWeekMg.toString())
                        onInput { e ->
                            val n = (e.target.asDynamic().value as String).toIntOrNull() ?: 0
                            onUserTargetsChange(userTargets.copy(omega3PerWeekMg = n.coerceAtLeast(0)))
                        }
                        style { width(80.px); padding(6.px, 10.px); borderRadius(DS.r8); border(1.5.px, LineStyle.Solid, Color(DS.border)); fontSize(13.px) }
                        attr("min", "0")
                    }
                    Span({ style { fontSize(12.px); color(Color(DS.inkLight)) }}) { Text("mg/week") }
                }
            }
        }
    }

    data class TagGroup(val key: String, val label: String, val emoji: String, val color: String, val bg: String)

    val tagGroups = listOf(
        TagGroup("Popular",     "Popular",      "⭐", DS.ink,      DS.tealFaint),
        TagGroup("HighProtein", "High Protein", "🥩", "#BF360C",   "#FFF3EE"),
        TagGroup("HighOmega3",  "High Omega-3", "🐟", "#1565C0",   "#EAF2FF"),
        TagGroup("Lean",        "Lean",         "🌿", DS.tealDeep, DS.tealGhost),
        TagGroup("KidsFriendly","Kids",         "👶", "#AD1457",   "#FFF0F5"),
        TagGroup("waterType",   "Water Type",   "🌊", "#1565C0",   "#E3F2FD"),
        TagGroup("boneType",    "Bone Type",    "🦴", "#6D4C41",   "#F5EDE8"),
        TagGroup("cutType",     "Cut Type",     "🔪", "#37474F",   "#ECEFF1"),
        TagGroup("cookStyle",   "Cooking",      "🍳", "#E65100",   "#FFF3EE"),
        TagGroup("benefit",     "Benefits",     "💪", DS.tealDeep, DS.tealGhost),
        TagGroup("diet",        "Diet",         "🥗", DS.tealDeep, DS.tealFaint),
        TagGroup("Medicinal",   "Medicinal",    "💊", "#4527A0",   "#F0EDFF")
    )

    val popularIds = allProducts.take(8).map { it.id }.toSet()

    fun productMatchesTag(p: Product, key: String, value: String): Boolean = when (key) {
        "Popular"      -> popularIds.contains(p.id)
        "HighOmega3"   -> p.tags.omega3.any { it.contains("high", ignoreCase = true) } || p.tags.waterType.any { it.contains("salt", ignoreCase = true) }
        "HighProtein"  -> p.tags.benefit.any { it.contains("protein", ignoreCase = true) }
        "Lean"         -> p.tags.diet.any { it.contains("lean", ignoreCase = true) } || p.tags.benefit.any { it.contains("lean", ignoreCase = true) }
        "KidsFriendly" -> p.tags.handling.any { it.contains("kid", ignoreCase = true) } || p.tags.diet.any { it.contains("kid", ignoreCase = true) }
        "Medicinal"    -> p.tags.benefit.any { it.contains("medicinal", ignoreCase = true) } || p.tags.benefit.any { it.contains("therapeutic", ignoreCase = true) }
        "waterType"    -> if (value.isBlank()) p.tags.waterType.isNotEmpty() else p.tags.waterType.any { it.equals(value, ignoreCase = true) }
        "boneType"     -> if (value.isBlank()) p.tags.boneType.isNotEmpty() else p.tags.boneType.any { it.equals(value, ignoreCase = true) }
        "cutType"      -> if (value.isBlank()) p.tags.cutType.isNotEmpty() else p.tags.cutType.any { it.equals(value, ignoreCase = true) }
        "cookStyle"    -> if (value.isBlank()) p.tags.cookStyle.isNotEmpty() else p.tags.cookStyle.any { it.equals(value, ignoreCase = true) }
        "benefit"      -> if (value.isBlank()) p.tags.benefit.isNotEmpty() else p.tags.benefit.any { it.equals(value, ignoreCase = true) }
        "diet"         -> if (value.isBlank()) p.tags.diet.isNotEmpty() else p.tags.diet.any { it.equals(value, ignoreCase = true) }
        else           -> true
    }

    val activeGroup = tagGroups.find { it.key == activeTagKey } ?: tagGroups.first()

    val subValues: List<String> = when (activeTagKey) {
        "waterType" -> allProducts.flatMap { it.tags.waterType }.distinct().sorted()
        "boneType"  -> allProducts.flatMap { it.tags.boneType }.distinct().sorted()
        "cutType"   -> allProducts.flatMap { it.tags.cutType }.distinct().sorted()
        "cookStyle" -> allProducts.flatMap { it.tags.cookStyle }.distinct().sorted()
        "benefit"   -> allProducts.flatMap { it.tags.benefit }.distinct().sorted()
        "diet"      -> allProducts.flatMap { it.tags.diet }.distinct().sorted()
        else        -> emptyList()
    }

    val filtered = allProducts.filter { p ->
        productMatchesTag(p, activeTagKey, activeTagVal) &&
                (searchQuery.isBlank() || p.name.contains(searchQuery, ignoreCase = true))
    }

    // Per-unit estimates: 1 unit ≈ 500g (Borshi standard). 500g fish: ~100g protein, ~500 cal, ~2500mg ω3 (saltwater) / ~500mg (freshwater)
    val totalProtein  = selected.entries.sumOf { (id, qty) ->
        val p = allProducts.find { it.id == id }
        val perUnit = if (p?.tags?.benefit?.any { it.contains("protein", ignoreCase = true) } == true) 100 else 80
        qty * perUnit
    }
    val totalCalories = selected.entries.sumOf { (id, qty) ->
        val p = allProducts.find { it.id == id }
        val perUnit = if (p?.tags?.diet?.any { it.contains("lean", ignoreCase = true) } == true) 400 else 500
        qty * perUnit
    }
    val totalOmega3   = selected.entries.sumOf { (id, qty) ->
        val p = allProducts.find { it.id == id }
        val perUnit = if (p?.tags?.waterType?.any { it.contains("salt", ignoreCase = true) } == true) 2500 else 500
        qty * perUnit
    }
    val totalCost     = selected.entries.sumOf { (id, qty) -> (allProducts.find { it.id == id }?.price ?: 0.0) * qty }
    val totalItems    = selected.values.sum()

    // Target progress: protein goal is weekly (7× daily), budget is weekly
    val proteinTargetWeekly = userTargets.proteinPerDayGrams * 7
    val proteinPct = if (proteinTargetWeekly > 0) (totalProtein.toDouble() / proteinTargetWeekly).coerceAtMost(1.5) else 0.0
    val omega3Target = userTargets.omega3PerWeekMg
    val omega3Pct = if (omega3Target > 0) (totalOmega3.toDouble() / omega3Target).coerceAtMost(1.5) else 0.0
    val overBudget = userTargets.hasBudgetCeiling() && totalCost > userTargets.budgetPerWeekRupees

    // ── Stats bar (with target progress + budget guard) ─────────────────────
    Div({ classes("stat-bar"); style { marginBottom(DS.sp24) }}) {
        data class StatItem(val emoji: String, val label: String, val value: String, val sub: String, val active: Boolean, val warn: Boolean = false, val showProgress: Boolean = false, val progressPct: Double = 0.0)
        listOf(
            StatItem("🥩", "Protein",  "${totalProtein}g${if (proteinTargetWeekly > 0) " / ${proteinTargetWeekly}g" else ""}",  if (proteinTargetWeekly > 0) "goal" else "weekly est.", totalProtein > 0, false, proteinTargetWeekly > 0 && totalProtein > 0, proteinPct),
            StatItem("🔥", "Calories", "$totalCalories",        "per unit",    totalCalories > 0, false, false, 0.0),
            StatItem("🐟", "Omega-3",  "${totalOmega3}mg${if (omega3Target > 0) " / ${omega3Target}mg" else ""}", if (omega3Target > 0) "goal" else "weekly est.", totalOmega3 > 0, false, omega3Target > 0 && totalOmega3 > 0, omega3Pct),
            StatItem("🛒", "Items",    "$totalItems",            "in your mix", totalItems    > 0, false, false, 0.0),
            StatItem("💰", "Cost",     "₹${totalCost.toInt()}${if (userTargets.hasBudgetCeiling()) " / ₹${userTargets.budgetPerWeekRupees.toInt()}" else ""}", if (userTargets.hasBudgetCeiling()) "budget" else "total", totalCost > 0, overBudget, false, 0.0)
        ).forEach { s ->
            Div({ attr("class", if (s.active) "stat-cell active" else "stat-cell"); style { if (s.warn) backgroundColor(Color("#FFEBEE")) else {} } }) {
                Div({ style {
                    fontSize(18.px); marginBottom(4.px)
                    property("filter", if (s.active) "none" else "grayscale(1) opacity(0.35)")
                    property("transition","filter 0.3s ease")
                }}) { Text(s.emoji) }

                Div({
                    attr("key", s.value)
                    classes("stat-value")
                    style {
                        fontFamily("Syne, sans-serif"); fontSize(18.px); fontWeight("800")
                        color(when {
                            s.warn -> Color(DS.crimson)
                            s.active -> Color(DS.teal)
                            else -> Color(DS.borderDark)
                        })
                        property("transition","color 0.3s ease")
                    }
                }) { Text(s.value) }

                Div({ style { fontSize(11.px); fontWeight("600"); color(Color(DS.ink)); marginTop(1.px) }}) { Text(s.label) }
                Div({ style { fontSize(10.px); color(Color(DS.inkLight)) }}) { Text(s.sub) }
                // Protein or Omega-3 progress bar (when target set)
                if (s.showProgress && s.progressPct > 0) {
                    Div({ style {
                        marginTop(6.px); height(4.px); borderRadius(2.px)
                        backgroundColor(Color(DS.border))
                        overflow("hidden")
                    }}) {
                        Div({ style {
                            width((100.0 * s.progressPct.coerceIn(0.0, 1.0)).percent)
                            height(100.percent)
                            backgroundColor(if (s.progressPct >= 1) Color(DS.teal) else Color(DS.tealDeep))
                        }}) {}
                    }
                }
            }
        }
    }

    // ── Search ────────────────────────────────────────────────────────────
    Div({ style { position(Position.Relative); marginBottom(DS.sp16) }}) {
        Div({ style {
            position(Position.Absolute); left(14.px); top(50.percent)
            property("transform","translateY(-50%)")
            property("pointer-events","none"); fontSize(15.px)
        }}) { Text("🔍") }
        Input(org.jetbrains.compose.web.attributes.InputType.Text) {
            value(searchQuery)
            onInput { searchQuery = it.value }
            style {
                width(100.percent); padding(13.px, 40.px, 13.px, 42.px)
                borderRadius(DS.r12); border(1.5.px, LineStyle.Solid, Color(DS.border))
                fontSize(14.px); property("outline","none"); color(Color(DS.ink))
                backgroundColor(Color(DS.white)); fontFamily("DM Sans, sans-serif")
            }
            attr("placeholder","Search products…")
        }
        if (searchQuery.isNotBlank()) {
            Span({
                style {
                    position(Position.Absolute); right(12.px); top(50.percent)
                    property("transform","translateY(-50%)")
                    cursor("pointer"); fontSize(15.px); color(Color(DS.inkLight))
                }
                onClick { searchQuery = "" }
            }) { Text("✕") }
        }
    }

    // ── Tag group pills ───────────────────────────────────────────────────
    Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp12) }}) {
        tagGroups.forEach { tg ->
            val isActive = activeTagKey == tg.key
            Span({
                classes("tag-chip")
                style {
                    backgroundColor(if (isActive) Color(tg.color) else Color(tg.bg))
                    color(if (isActive) Color(DS.white) else Color(tg.color))
                    property("border-color", if (isActive) tg.color else "${tg.color}44")
                }
                onClick { activeTagKey = tg.key; activeTagVal = "" }
            }) { Text("${tg.emoji} ${tg.label}") }
        }
    }

    // Sub-value chips
    if (subValues.isNotEmpty()) {
        Div({ style { display(DisplayStyle.Flex); gap(6.px); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp12); paddingLeft(DS.sp4) }}) {
            Span({
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    padding(4.px, 12.px); borderRadius(DS.rFull); fontSize(11.px); fontWeight("600"); cursor("pointer")
                    backgroundColor(if (activeTagVal.isBlank()) Color(activeGroup.color) else Color(activeGroup.bg))
                    color(if (activeTagVal.isBlank()) Color(DS.white) else Color(activeGroup.color))
                    border(1.px, LineStyle.Solid, Color(activeGroup.color))
                }
                onClick { activeTagVal = "" }
            }) { Text("All") }
            subValues.forEach { sv ->
                val isSvActive = activeTagVal == sv
                Span({
                    style {
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                        padding(4.px, 12.px); borderRadius(DS.rFull); fontSize(11.px); fontWeight("600"); cursor("pointer")
                        backgroundColor(if (isSvActive) Color(activeGroup.color) else Color(DS.white))
                        color(if (isSvActive) Color(DS.white) else Color(DS.inkMid))
                        border(1.px, LineStyle.Solid, if (isSvActive) Color(activeGroup.color) else Color(DS.border))
                    }
                    onClick { activeTagVal = sv }
                }) { Text(sv) }
            }
        }
    }

    Div({ style { fontSize(12.px); color(Color(DS.inkLight)); marginBottom(DS.sp12); fontWeight("500") }}) {
        Text("${filtered.size} product${if (filtered.size != 1) "s" else ""} · ${activeGroup.emoji} ${activeGroup.label}")
    }

    // ── Products grid ─────────────────────────────────────────────────────
    // Show one row visible by default; expand arrow if more than one row
    if (allProducts.isEmpty()) {
        EmptyState("Loading products…")
    } else if (filtered.isEmpty()) {
        EmptyState("No products match this filter. Try another tag!")
    } else {
        val itemsPerRow = 5
        val rowCount = (filtered.size + itemsPerRow - 1) / itemsPerRow
        var expanded by remember { mutableStateOf(rowCount <= 1) }
        Div({ style { marginBottom(if (totalItems > 0) 80.px else 0.px) }}) {
            Div({ style {
                display(DisplayStyle.Grid)
                property("grid-template-columns","repeat(auto-fill,minmax(160px,1fr))")
                gap(DS.sp12)
                if (!expanded) property("max-height","220px")
                overflow("hidden")
            }}) {
                filtered.forEach { product ->
                    val qty = selected[product.id] ?: 0
                    val score = matchScore(product, userTargets)
                    DiyProductTile(
                        product   = product,
                        qty       = qty,
                        matchScore = if (score > 0) score else null,
                        onAdd     = { selected = selected.toMutableMap().also { m -> m[product.id] = (m[product.id] ?: 0) + 1 } },
                        onRemove  = { selected = selected.toMutableMap().also { m ->
                            val n = (m[product.id] ?: 1) - 1
                            if (n <= 0) m.remove(product.id) else m[product.id] = n
                        }}
                    )
                }
            }
            if (rowCount > 1 && !expanded) {
                Button({
                    style {
                        width(100.percent)
                        marginTop(DS.sp12)
                        padding(12.px)
                        backgroundColor(Color.transparent)
                        color(Color(DS.teal))
                        border(1.px, LineStyle.Solid, Color(DS.border))
                        borderRadius(DS.r12)
                        fontSize(14.px)
                        fontWeight("600")
                        cursor("pointer")
                    }
                    onClick { expanded = true }
                }) { Text("▼ Show all ${filtered.size} products") }
            }
        }
    }

    // ── Sticky order bar ──────────────────────────────────────────────────
    if (totalItems > 0) {
        var deliveryMode by remember { mutableStateOf("onetime") } // onetime | weekly | biweekly | subscription
        Div({ classes("order-bar") }) {
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) }}) {
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(16.px)
                    fontWeight("800"); color(Color(DS.white))
                }}) {
                    Text("$totalItems item${if (totalItems != 1) "s" else ""} · ₹${totalCost.toInt()}")
                }
                Div({ style { fontSize(11.px); color(Color("rgba(255,255,255,0.6)")); marginTop(0.px) }}) {
                    Text("~${totalProtein}g protein · ${totalCalories} cal · ${totalOmega3}mg ω-3")
                }
                // One Time | Weekly | Bi-weekly | Subscription
                Div({ style { display(DisplayStyle.Flex); gap(6.px); marginTop(4.px); flexWrap(FlexWrap.Wrap) }}) {
                    listOf(
                        "onetime" to "One Time",
                        "weekly" to "Weekly",
                        "biweekly" to "Bi-weekly",
                        "subscription" to "Subscription"
                    ).forEach { (key, label) ->
                        Span({
                            style {
                                padding(4.px, 10.px); borderRadius(DS.rFull); fontSize(11.px); fontWeight("600")
                                cursor("pointer")
                                backgroundColor(if (deliveryMode == key) Color("rgba(255,255,255,0.3)") else Color("rgba(255,255,255,0.1)"))
                                color(Color(DS.white))
                            }
                            onClick { deliveryMode = key }
                        }) { Text(label) }
                    }
                }
            }
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.FlexEnd); gap(6.px) }}) {
                Button({
                    style {
                        padding(12.px, 28.px); borderRadius(DS.rFull); border(0.px)
                        property("background","linear-gradient(135deg,${DS.crimson},#FF4D72)")
                        color(Color(DS.white)); fontSize(14.px); fontWeight("800"); cursor("pointer")
                        fontFamily("DM Sans, sans-serif")
                        property("box-shadow","0 4px 16px rgba(209,18,67,0.45)")
                    }
                    onClick {
                        if (cartStore != null && onNavigate != null) {
                            val isOneTime = deliveryMode == "onetime"
                            selected.entries.forEach { (productId, qty) ->
                                val product = allProducts.find { it.id == productId } ?: return@forEach
                                if (isOneTime) {
                                    repeat(qty) { cartStore.addProduct(product, "500g") }
                                } else {
                                    val freq = when (deliveryMode) {
                                        "weekly" -> com.example.borshi.web.cart.OrderFrequency.WEEKLY
                                        "biweekly" -> com.example.borshi.web.cart.OrderFrequency.BIWEEKLY
                                        else -> com.example.borshi.web.cart.OrderFrequency.WEEKLY
                                    }
                                    cartStore.addSubscription(
                                        product = product,
                                        packSize = "500g",
                                        qtyPerDelivery = qty,
                                        schedule = com.example.borshi.web.cart.SubscriptionSchedule(
                                            frequency = freq,
                                            startDate = kotlin.js.Date().getTime().toLong(),
                                            endDate = null,
                                            slot = com.example.borshi.web.cart.DeliverySlot.MORNING
                                        )
                                    )
                                }
                            }
                            onNavigate(com.example.borshi.web.Screen.Cart)
                        } else {
                            kotlinx.browser.window.alert("Please sign in or refresh to add items to cart.")
                        }
                    }
                }) { Text("Order My Mix →") }
                Span({
                    style {
                        fontSize(10.px); color(Color("rgba(255,255,255,0.7)")); cursor("pointer")
                        property("text-decoration","underline")
                    }
                    onClick { kotlinx.browser.window.alert("Contact us to skip your next delivery.") }
                }) { Text("Skip next delivery") }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
/** Match score 0-100 based on product tags vs user goal + micronutrient focus. Uses existing tags. */
private fun matchScore(product: Product, targets: UserTargets): Int {
    val hp = product.tags.benefit.any { it.contains("protein", ignoreCase = true) }
    val lean = product.tags.diet.any { it.contains("lean", ignoreCase = true) } || product.tags.benefit.any { it.contains("lean", ignoreCase = true) }
    val omega3 = product.tags.omega3.isNotEmpty() || product.tags.waterType.any { it.contains("salt", ignoreCase = true) }
    val highOmega3 = product.tags.omega3.any { it.contains("high", ignoreCase = true) } || product.tags.waterType.any { it.contains("salt", ignoreCase = true) }
    fun hasMineral(m: String) = product.tags.minerals.any { it.contains(m, ignoreCase = true) }
    fun hasVitamin(v: String) = product.tags.vitamins.any { it.contains(v, ignoreCase = true) }
    val hasB12 = hasVitamin("b12") || hasVitamin("b_12")
    val hasVitD = hasVitamin("d") || hasVitamin("vitamin_d")
    val hasZinc = hasMineral("zinc")
    val hasSelenium = hasMineral("selenium")
    val hasIodine = hasMineral("iodine")
    val hasMagnesium = hasMineral("magnesium")
    val lowCarb = product.tags.diet.any { it.contains("low", ignoreCase = true) } || product.tags.diet.any { it.contains("lean", ignoreCase = true) }
    val lowMercury = product.tags.benefit.any { it.contains("low", ignoreCase = true) } || product.tags.handling.any { it.contains("pregnancy", ignoreCase = true) } || product.tags.waterType.any { it.contains("fresh", ignoreCase = true) }

    var base = 50
    var bonus = when (targets.goalPreset) {
        PlanCategory.MUSCLE_BUILDING -> if (hp) 25 else if (lean) 10 else 0
        PlanCategory.HEART_HEALTH, PlanCategory.PREGNANCY -> if (omega3) 25 else if (hp) 8 else 0
        PlanCategory.WEIGHT_LOSS -> if (lean) 25 else if (hp) 10 else 0
        PlanCategory.ATHLETE -> if (hp) 20 else if (lean) 8 else 0
        PlanCategory.DIABETES -> if (lowCarb && hp) 25 else if (hp) 10 else 0
        PlanCategory.SENIOR, PlanCategory.GENERAL_HEALTH -> if (omega3 || hp) 15 else 0
    }
    // Micronutrient focus bonuses (up to +25 total)
    if (targets.maximizeOmega3 && highOmega3) bonus += 12
    if (targets.prioritizeB12 && hasB12) bonus += 8
    if (targets.prioritizeVitaminD && hasVitD) bonus += 8
    if (targets.prioritizeZinc && hasZinc) bonus += 6
    if (targets.prioritizeSelenium && hasSelenium) bonus += 6
    if (targets.prioritizeIodine && hasIodine) bonus += 5
    if (targets.prioritizeMagnesium && hasMagnesium) bonus += 5
    if (targets.lowMercuryPregnancy && lowMercury) bonus += 10
    if (targets.lowCarbDiabetes && lowCarb) bonus += 8
    return (base + bonus).coerceIn(0, 100)
}

// DIY Product Tile
// FIX #8: shows up to 2 mini nutrition tags derived from product.tags
// Phase 1: matchScore badge when user has set a goal
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DiyProductTile(product: Product, qty: Int, matchScore: Int?, onAdd: () -> Unit, onRemove: () -> Unit) {
    val isSelected = qty > 0

    // FIX #8: derive up to 2 tags from actual product data
    val miniTags = buildList {
        if (product.tags.benefit.any    { it.contains("protein",     ignoreCase = true) }) add("🥩" to "High Protein")
        if (product.tags.waterType.any  { it.contains("salt",        ignoreCase = true) }) add("🌊" to "Saltwater")
        else if (product.tags.waterType.any { it.contains("fresh",   ignoreCase = true) }) add("💧" to "Freshwater")
        if (product.tags.diet.any       { it.contains("lean",        ignoreCase = true) }) add("🌿" to "Lean")
        if (product.tags.omega3.isNotEmpty())                                               add("🐟" to "Omega-3")
    }

    Div({ attr("class", if (isSelected) "diy-tile selected" else "diy-tile") }) {
        if (isSelected) {
            Div({ style {
                position(Position.Absolute); top(8.px); right(8.px)
                width(20.px); height(20.px); borderRadius(50.percent)
                backgroundColor(Color(DS.teal)); color(Color(DS.white))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center); fontSize(11.px); fontWeight("800")
            }}) { Text("✓") }
        }
        if (matchScore != null && matchScore >= 70) {
            Div({ style {
                position(Position.Absolute); top(8.px); left(8.px)
                padding(3.px, 8.px); borderRadius(DS.rFull)
                backgroundColor(Color(DS.teal)); color(Color(DS.white))
                fontSize(10.px); fontWeight("800"); fontFamily("DM Sans, sans-serif")
            }}) { Text("${matchScore}% match") }
        }

        // Image
        Div({ style {
            width(72.px); height(72.px); borderRadius(DS.r12)
            overflow("hidden"); flexShrink(0); backgroundColor(Color(DS.tealGhost))
        }}) {
            if (product.imageUrl.isNotEmpty()) {
                Img(src = product.imageUrl, attrs = {
                    style { width(100.percent); height(100.percent); property("object-fit","cover") }
                    attr("onerror","this.parentElement.innerHTML='<div style=\"font-size:32px;display:flex;align-items:center;justify-content:center;height:100%\">🐟</div>'")
                })
            } else {
                Div({ style {
                    width(100.percent); height(100.percent)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center); fontSize(32.px)
                }}) { Text("🐟") }
            }
        }

        // Name
        Div({ style {
            fontSize(12.px); fontWeight("700"); color(Color(DS.ink))
            lineHeight("1.3"); width(100.percent)
        }}) { Text(product.name) }

        // FIX #8: mini nutrition tags — max 2
        if (miniTags.isNotEmpty()) {
            Div({ style {
                display(DisplayStyle.Flex); gap(4.px)
                flexWrap(FlexWrap.Wrap); justifyContent(JustifyContent.Center)
            }}) {
                miniTags.take(2).forEach { (emoji, label) ->
                    Span({ style {
                        fontSize(9.px); fontWeight("700")
                        padding(2.px, 6.px); borderRadius(DS.rFull)
                        backgroundColor(Color(DS.tealGhost)); color(Color(DS.tealDeep))
                    }}) { Text("$emoji $label") }
                }
            }
        }

        // Price
        Div({ style {
            fontFamily("Syne, sans-serif"); fontSize(14.px)
            color(Color(DS.crimson)); fontWeight("800")
        }}) { Text("₹${product.price.toInt()}") }

        // Qty control
        if (qty == 0) {
            Button({
                style {
                    width(100.percent); padding(7.px); borderRadius(DS.r8); border(0.px)
                    backgroundColor(Color(DS.teal)); color(Color(DS.white))
                    fontSize(13.px); fontWeight("700"); cursor("pointer")
                    fontFamily("DM Sans, sans-serif")
                }
                onClick { onAdd() }
            }) { Text("+ Add") }
        } else {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(DS.sp8) }}) {
                Button({
                    style {
                        width(30.px); height(30.px); borderRadius(50.percent); border(0.px)
                        backgroundColor(Color("#FFEBEE")); color(Color(DS.crimson))
                        fontWeight("800"); cursor("pointer"); fontSize(18.px)
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center); fontFamily("DM Sans, sans-serif")
                    }
                    onClick { onRemove() }
                }) { Text("−") }
                Span({ style {
                    fontFamily("Syne, sans-serif"); fontWeight("800"); fontSize(16.px)
                    color(Color(DS.ink)); minWidth(20.px); textAlign("center")
                }}) { Text("$qty") }
                Button({
                    style {
                        width(30.px); height(30.px); borderRadius(50.percent); border(0.px)
                        backgroundColor(Color(DS.tealGhost)); color(Color(DS.tealDeep))
                        fontWeight("800"); cursor("pointer"); fontSize(18.px)
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center); fontFamily("DM Sans, sans-serif")
                    }
                    onClick { onAdd() }
                }) { Text("+") }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Category helpers
// ─────────────────────────────────────────────────────────────────────────────
private fun planCategoryEmoji(cat: PlanCategory): String = when (cat) {
    PlanCategory.MUSCLE_BUILDING -> "💪"
    PlanCategory.WEIGHT_LOSS     -> "🔥"
    PlanCategory.GENERAL_HEALTH  -> "🌿"
    PlanCategory.PREGNANCY       -> "🤰"
    PlanCategory.SENIOR          -> "👴"
    PlanCategory.ATHLETE         -> "🏃"
    PlanCategory.DIABETES        -> "💉"
    PlanCategory.HEART_HEALTH    -> "❤️"
}

private fun planCategoryStyle(cat: PlanCategory): Pair<String, String> = when (cat) {
    PlanCategory.MUSCLE_BUILDING -> "#FFF3E0" to "#E65100"
    PlanCategory.WEIGHT_LOSS     -> "#FCE4EC" to "#C62828"
    PlanCategory.GENERAL_HEALTH  -> "#E8F5E9" to "#2E7D32"
    PlanCategory.PREGNANCY       -> "#FCE4EC" to "#880E4F"
    PlanCategory.SENIOR          -> "#E8EAF6" to "#283593"
    PlanCategory.ATHLETE         -> DS.tealGhost to DS.tealDeep
    PlanCategory.DIABETES        -> "#FFF8E1" to "#F57F17"
    PlanCategory.HEART_HEALTH    -> "#FFEBEE" to "#B71C1C"
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy alias
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun PlanCard(plan: NutritionistBasePlan, onClick: (NutritionistBasePlan) -> Unit) {
    PlanCard(plan, DS.teal, isMonthly = false, onViewPlan = onClick)
}